import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleById } from '@/mocks/articles';
import { categories } from '@/mocks/articles';
import { siteConfig } from '@/mocks/home';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const category = categories.find((c) => c.slug === article?.category);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-6">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的内容可能已被删除或链接无效</p>
            <Link
              to="/"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    image: article.cover,
    publisher: { '@type': 'Organization', name: siteConfig.name },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
      { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `${siteConfig.url}/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `${siteConfig.url}/article/${article.id}` },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              {category && (
                <>
                  <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap">{category.name}</Link>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </>
              )}
              <span className="text-foreground-700 font-medium">{article.title}</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.readers}人阅读
              </span>
              <span className="flex items-center gap-1 text-accent-600">
                <i className="ri-star-fill text-accent-500"></i>
                {article.rating}分
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src={article.cover}
              alt={article.title}
              className="w-full h-auto object-cover object-top max-h-[400px]"
              title={`${article.title} - po18官网`}
            />
          </div>

          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-10">
            <div className="prose prose-lg max-w-none">
              {article.content.split('\n\n').map((paragraph, idx) => {
                if (paragraph.startsWith('第') && paragraph.includes('章')) {
                  return (
                    <h2 key={idx} className="font-heading text-xl font-bold text-foreground-950 mt-8 mb-4">
                      {paragraph}
                    </h2>
                  );
                }
                return (
                  <p key={idx} className="text-foreground-700 leading-loose mb-4 text-base">
                    {paragraph}
                  </p>
                );
              })}
            </div>
          </div>

          <div className="mt-10 flex items-center justify-between">
            <Link
              to={category ? `/category/${article.category}` : '/'}
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors duration-200"
            >
              <i className="ri-arrow-left-line"></i>
              {category ? `返回${category.name}分类` : '返回首页'}
            </Link>
            <div className="flex items-center gap-3">
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors duration-200 cursor-pointer">
                <i className="ri-heart-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors duration-200 cursor-pointer">
                <i className="ri-share-forward-line"></i>
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}